<!--
/**
 * 消息卡片组件 - MessageCard.vue
 * 
 * @description 一个功能完整的消息通知卡片组件，支持以下特性：
 * - 消息基本信息展示（时间、标题、内容）
 * - 未读状态指示器
 * - 智能内容展开/收起功能（基于内容长度自动判断）
 * - 可选的删除按钮
 * - 完整的事件系统（点击、删除、展开、收起）
 * - 响应式设计适配
 * 
 * @features
 * - ✅ 自动检测内容是否需要展开功能
 * - ✅ ResizeObserver 监听内容变化
 * - ✅ 优雅的动画过渡效果
 * - ✅ TypeScript 类型安全
 * - ✅ 组合式 API 架构
 * 
 * @usage
 * <MessageCard
 *   time="2024/08/31 16:30:45"
 *   title="消息标题"
 *   content="消息内容..."
 *   :is-unread="true"
 *   :show-delete-btn="true"
 *   :show-expand-btn="true"
 *   @click="handleClick"
 *   @delete="handleDelete"
 * />
 */
-->
<template>
    <!-- 消息卡片容器 - 支持自定义样式类和点击事件 -->
    <div class="message-card" @click="handleCardClick">
        <!-- 头部区域：时间信息 + 未读状态指示器 + 展开控制按钮 -->
        <div class="message-header">
            <!-- 左侧：时间和未读状态 -->
            <div class="time-section">
                <span class="time-text">{{ time }}</span>
                <!-- 未读消息指示器 - 绿色小圆点 -->
                <div v-if="isUnread" class="unread-indicator"></div>
            </div>
            <!-- 右侧：展开/收起按钮 - 仅在启用展开功能且内容需要展开时显示 -->
            <div v-if="showExpandBtn && needsExpansion" class="expand-btn" @click.stop="toggleExpand">
                {{ isExpanded ? $t('hide') : $t('show_all') }}
                <van-icon :name="isExpanded ? 'arrow-up' : 'arrow-down'" />
            </div>
        </div>

        <!-- 消息标题 - 单行显示，超出部分用省略号 -->
        <div class="message-title">{{ title }}</div>

        <!-- 消息内容 - 支持展开/收起，通过CSS类控制显示状态 -->
        <div ref="contentRef" :class="['message-content', getContentClasses()]">
            {{ content }}
        </div>

        <!-- 删除按钮容器 - 可选显示，右对齐 -->
        <div v-if="showDeleteBtn" class="delete-btn-container">
            <div class="delete-btn" @click.stop="handleDelete">
                <svg aria-hidden="true">
                    <use data-v-ee3a47c8="" href="#icon-svg-DeleteIcon" fill="#b3bec1"></use>
                </svg>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import type { MessageCardProps, MessageCardEmits } from '@/components/message/types'
import { useMessageCard } from '@/components/message/useMessageCard'

/**
 * ======================== 组件属性定义 ========================
 */
// Props - 组件接收的属性，带有默认值
const props = withDefaults(defineProps<MessageCardProps>(), {
    isUnread: false, // 默认为已读状态
    showDeleteBtn: true, // 默认显示删除按钮
    showExpandBtn: true, // 默认显示展开按钮
})

// Emits - 组件可触发的事件
const emit = defineEmits<MessageCardEmits>()

/**
 * ======================== 响应式数据 ========================
 */
// 模板引用 - 用于获取内容DOM元素，进行高度检测
const contentRef = ref<HTMLElement>()

// ResizeObserver清理函数 - 用于组件卸载时清理观察器
let cleanupObserver: (() => void) | undefined

/**
 * ======================== 组合式函数 ========================
 */
// 使用消息卡片展开/收起逻辑的组合式函数
const {
    isExpanded, // 当前是否展开状态
    needsExpansion, // 是否需要展开功能（内容是否超过3行）
    isChecking, // 是否正在检测中
    checkNeedsExpansion, // 检测是否需要展开功能的方法
    observeContentChanges, // 监听内容变化的方法
    toggleExpansion, // 切换展开/收起状态的方法
    getContentClasses, // 获取内容样式类的方法
} = useMessageCard(contentRef, {
    maxLines: 3, // 最大显示行数
    enableExpansion: props.showExpandBtn, // 是否启用展开功能
})

/**
 * ======================== 事件处理函数 ========================
 */

/**
 * 处理展开/收起按钮点击
 * 切换内容显示状态并触发相应事件
 */
const toggleExpand = () => {
    const expanded = toggleExpansion()

    // 根据展开状态触发对应事件
    if (expanded) {
        emit('expand') // 触发展开事件
    } else {
        emit('collapse') // 触发收起事件
    }
}

/**
 * 处理删除按钮点击
 * 阻止事件冒泡，只触发删除事件
 */
const handleDelete = () => {
    emit('delete')
}

/**
 * 处理卡片点击
 * 点击卡片主体区域时触发
 */
const handleCardClick = () => {
    emit('click')
}

/**
 * ======================== 监听器 ========================
 */

/**
 * 监听内容变化
 * 当内容发生变化时，重新检测是否需要展开功能
 */
watch(
    () => props.content,
    async () => {
        if (props.showExpandBtn) {
            await checkNeedsExpansion()
        }
    },
    { immediate: false }
)

/**
 * 监听展开按钮显示状态变化
 * 当展开按钮显示状态改变时，启用或停用相关功能
 */
watch(
    () => props.showExpandBtn,
    async (newVal) => {
        if (newVal) {
            // 启用展开功能：检测内容并开始监听变化
            await checkNeedsExpansion()
            cleanupObserver = observeContentChanges()
        } else {
            // 禁用展开功能：清理监听器
            cleanupObserver?.()
        }
    },
    { immediate: false }
)

/**
 * ======================== 生命周期钩子 ========================
 */

/**
 * 组件挂载时的初始化
 * 如果启用了展开功能，则进行初始检测并开始监听
 */
onMounted(async () => {
    if (props.showExpandBtn) {
        await checkNeedsExpansion()
        cleanupObserver = observeContentChanges()
    }
})

/**
 * 组件卸载时的清理
 * 清理ResizeObserver，避免内存泄漏
 */
onUnmounted(() => {
    cleanupObserver?.()
})
</script>

<style lang="scss" scoped>
/**
 * ======================== 消息卡片样式 ========================
 */

/* 主容器样式 */
.message-card {
    position: relative;
    padding: 14px 21px;
    margin-bottom: 21px;
    background-color: #323738;
    border-radius: 14px;

    /* ========== 头部区域样式 ========== */
    .message-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;

        /* 左侧时间区域 */
        .time-section {
            display: flex;
            align-items: center;
            gap: 8px; /* 时间文字与未读指示器的间距 */

            .time-text {
                font-size: 24px;
                font-weight: bold;
                color: #b3bec1;
            }

            /* 未读状态指示器 */
            .unread-indicator {
                width: 12px;
                height: 12px;
                background-color: #24ee89;
                border-radius: 50%;
                flex-shrink: 0;
            }
        }

        /* 右侧展开按钮 */
        .expand-btn {
            display: flex;
            align-items: center;
            gap: 8px; /* 文字与图标的间距 */
            padding: 4px 8px; /* 按钮内边距 */
            font-size: 20px;
            font-weight: bold;
            color: #fff;

            .van-icon {
                font-size: 18px; /* 图标大小 */
                font-weight: bold;
            }
        }
    }

    /* ========== 标题样式 ========== */
    .message-title {
        font-size: 28px;
        font-weight: bold;
        color: #fff;
        margin-bottom: 14px; /* 与内容的间距 */

        /* 单行截断效果 */
        display: -webkit-box;
        -webkit-line-clamp: 1; /* 限制显示1行 */
        line-clamp: 1; /* 标准属性（兼容性） */
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis; /* 超出部分显示省略号 */
        white-space: nowrap; /* 不换行 */
    }

    /* ========== 内容样式 ========== */
    .message-content {
        font-size: 24px;
        font-weight: bold;
        color: #b3bec1;
        line-height: 1.5;
        word-wrap: break-word; /* 长单词自动换行 */
        white-space: pre-wrap; /* 保留换行符和空格 */
        transition: max-height 0.3s ease; /* 展开/收起动画 */

        /* 收起状态 - 最多显示3行 */
        &.message-content-collapsed {
            display: -webkit-box;
            -webkit-line-clamp: 3; /* 限制显示3行 */
            line-clamp: 3; /* 标准属性（兼容性） */
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        /* 展开状态 - 显示全部内容 */
        &.message-content-expanded {
            -webkit-line-clamp: unset; /* 取消行数限制 */
            line-clamp: unset; /* 标准属性（兼容性） */
            overflow: visible;
        }

        /* 检测状态 - 半透明效果 */
        &.message-content-checking {
            opacity: 0.8; /* 检测时的视觉反馈 */
        }
    }

    /* ========== 删除按钮样式 ========== */
    .delete-btn-container {
        display: flex;
        justify-content: flex-end;
        margin-top: 12px;
        margin-bottom: 14px;

        .delete-btn {
            width: 44px;
            height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;

            .van-icon {
                font-size: 42px;
            }
        }
    }
}
</style>
